/**
 *  Плагин скрывающего контента
 *
 *  Плагин требует структуры html.
 *
 *  Элементы:
 *  $holder - Верхний контейнер
 *  $contentHolder - Обёртка контента, ограничивает контент по высоте и обрезает
 *  $content - контент, имеет полную высоту
 *  $toggle - кнопка показа/скрытия
 *  $toggleText - Текста на кнопке
 *
 *
 *  Опции:
 *  toggle.openText - текст на кнопке для открытия
 *  toggle.closeText - текст на кнопке для закрытия
 *  selector - объект с селекторами элементов
 */
import {Helpers} from "../helpers/helpers";

type options = {
    selectors: {
        content: string,
        contentHolder: string,
        toggle: string,
        listItem: string,
    },
    mode: 'default' | 'list',
    listItemsMinAmount: number,
}

export default class CollapsableContent {
    private readonly OPENED_CLASS = 'opened';

    private readonly $holder: JQuery;
    private readonly $contentHolder: JQuery;
    private readonly $content: JQuery;
    private readonly $toggle: JQuery;
    private $listItems: JQuery|null = null;

    private isOpened = false;

    private options: options = {
        selectors: {
            content: '.js-collapsable-content',
            contentHolder: '.js-collapsable-content-holder',
            toggle: '.js-collapsable-toggle',
            listItem: '.js-collapsable-list-item',
        },
        mode: 'default',
        listItemsMinAmount: 10,
    };

    constructor($holder: JQuery, options: Partial<options> = {}) {
        this.$holder = $holder;

        this.options = {
            ...this.options,
            ...options
        };

        this.$contentHolder = this.$holder.find(this.options.selectors.contentHolder);
        this.$content = this.$holder.find(this.options.selectors.content);
        this.$toggle = this.$holder.find(this.options.selectors.toggle);

        this.init();
    }

    /**
     * Инициализация
     * @private
     */
    private init(): void
    {
        if (this.options.mode === 'list') {
            this.initListMode();
        }

        this.setStartSetting();
        this.initEvents();
    }

    /**
     * Иницилизировать события
     * @private
     */
    private initEvents(): void
    {
        this.$toggle.on('click', () => {
            if (this.isOpened) {
                this.closeHandle();
                return;
            }

            this.openHandle();
        });
    }

    /**
     * Обновить настройки плагина
     */
    public refresh(): void
    {
        this.setStartSetting();
    }

    /**
     * Переключить состояние
     */
    public toggleState(): void
    {
        this.isOpened ? this.closeHandle() : this.openHandle();
    }

    /**
     * Инициализировать режим списка
     * @private
     */
    private initListMode(): void
    {
        this.$listItems = this.$holder.find(this.options.selectors.listItem);
    }

    /**
     * Проверка на нужность сворачивания
     * @private
     */
    private isCollapsible(): boolean
    {
        let result = true;
        switch (this.options.mode) {
            case 'default':
                result = this.getMaxHeight() >= this.getMinHeight();
                break;
            case 'list':
                if (!this.$listItems) {
                    result = false;
                } else {
                    result = this.$listItems.length > this.options.listItemsMinAmount;
                }
        }
        return result;
    }

    /**
     * Отключить
     * @private
     */
    private disableCollapse(): void
    {
        this.$contentHolder.css({
            minHeight: '0px',
            maxHeight: 'none',
        });
        this.$holder.addClass(this.OPENED_CLASS);
        this.$toggle.hide();
    }

    /**
     * Активировать
     * @private
     */
    private activeCollapse(): void
    {
        this.$contentHolder.css({
            minHeight: `${this.getMinHeight()}px`,
            maxHeight: `${this.getMinHeight()}px`,
        });
        this.$holder.removeClass(this.OPENED_CLASS);
        this.$toggle.show();
    }

    /**
     * Установить настройки вначале
     * @private
     */
    private setStartSetting(): void
    {
        if (!this.isCollapsible()) {
            this.disableCollapse();
            return;
        }

        this.activeCollapse();
    }

    /**
     * проскролить к верху контейнера
     * @private
     */
    private scrollToTop(): void
    {
        if (this.isHolderTopInViewport()) {
            return;
        }

        Helpers.scrollTo(this.$holder.get(0) as HTMLHtmlElement);
    }

    /**
     * Получить минимальную высоту контента
     * @private
     */
    private getMinHeight(): number
    {
        let minHeight = 0;

        /**
         * Посчитать минимальную высоту итемов списка
         * @param $items
         */
        const calcListItemsMinHeight = ($items: JQuery) => {
            let result = 0;

            $items.each((index, element) => {
                result += $(element).outerHeight(true) ?? 0;
            });

            return result;
        };

        switch (this.options.mode) {
            case 'default':
                this.$contentHolder.css('minHeight', '');
                minHeight = Number(this.$contentHolder
                    .css('minHeight').replace(/[^0-9]/gi, '')) ?? 0;
                break;
            case 'list':
                minHeight = this.$listItems ?
                    calcListItemsMinHeight(this.$listItems.slice(0, this.options.listItemsMinAmount))
                    : 0;
        }

        return minHeight;
    }

    /**
     * Верх обёртки в видимой зоне экрана
     * @private
     */
    private isHolderTopInViewport(): boolean
    {
       return Helpers.isTopInViewPort(this.$holder);
    }

    /**
     * Получить максимальную высоту контента
     * @private
     */
    private getMaxHeight(): number
    {
        return this.$content.innerHeight() ?? 0;
    }

    /**
     * Обработать закрытие
     * @private
     */
    private closeHandle(): void
    {
        this.$contentHolder.css('maxHeight', `${this.getMinHeight()}px`);
        this.$holder.removeClass(this.OPENED_CLASS);
        this.isOpened = false;
        this.scrollToTop();
    }

    /**
     * Обработать открытие
     * @private
     */
    private openHandle(): void
    {
        this.$contentHolder.css('maxHeight', `${this.getMaxHeight()}px`);
        this.$holder.addClass(this.OPENED_CLASS);
        this.isOpened = true;
    }
}
